// S4 white-label brand bootstrap (§S4 4.W2).
//
// The SPA on `<slug>.drishalom.com` calls `GET /org-config` once on boot to
// resolve the org's brand (logo, colors, copy) and which anonymous entry
// points it exposes (guest 4.G2 / web self-onboarding 4.W3). `applyBrand`
// drives the live CSS custom properties defined in index.css, so a brand that
// arrives AFTER first paint still updates colors in place — no re-render. The
// resolved config is cached for the AuthScreen (guest/onboard entry points).

export type OrgBrand = {
  name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  font_family: string | null;
};

export type OrgConfig = {
  slug: string | null;
  name: string | null;
  brand: OrgBrand | null;
  guest_enabled: boolean;
  self_onboard_enabled: boolean;
};

const DEFAULT_CONFIG: OrgConfig = {
  slug: null,
  name: null,
  brand: null,
  guest_enabled: false,
  self_onboard_enabled: false,
};

let cached: OrgConfig = DEFAULT_CONFIG;

/** The org-config resolved at boot (default until /org-config returns). */
export const getOrgConfig = (): OrgConfig => cached;

// Apply a resolved org brand to the live document: CSS custom properties
// (read by every component via var(--…)), the document title, the theme-color
// meta, and the favicon. Idempotent and safe to call post-render — CSS vars
// cascade live, so colors update without a React re-render.
export const applyBrand = (cfg: OrgConfig): void => {
  cached = cfg;
  const b = cfg.brand;
  if (!b) return;
  const root = document.documentElement;
  if (b.primary_color) {
    root.style.setProperty('--color-primary', b.primary_color);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', b.primary_color);
  }
  // The user bubble is the brand's secondary accent; fall back to leaving the
  // built-in deep-blue when no secondary is set.
  if (b.secondary_color) {
    root.style.setProperty('--color-user-bubble', b.secondary_color);
  }
  if (b.font_family) {
    root.style.setProperty(
      '--font-sans',
      `"${b.font_family}", "Heebo", system-ui, -apple-system, sans-serif`,
    );
  }
  const title = b.name ?? cfg.name;
  if (title) document.title = title;
  if (b.logo_url) {
    const icon = document.querySelector('link[rel="icon"]');
    if (icon) icon.setAttribute('href', b.logo_url);
  }
};

// Fetch /org-config and apply the brand. Best-effort: any failure (network,
// non-2xx, parse) silently keeps the built-in default theme. Resolves once the
// brand is applied (or skipped), so main.tsx can race it against a short cap to
// brand-before-paint without risking a blank screen on a slow/absent backend.
export const bootstrapBrand = async (): Promise<void> => {
  try {
    const res = await fetch('/org-config', { credentials: 'same-origin' });
    if (!res.ok) return;
    const cfg = (await res.json()) as OrgConfig;
    applyBrand(cfg);
  } catch {
    // keep defaults
  }
};
